// @givmo/board — every word the connector's two proposal tools add to Board's pages (CBG-AI5-PROPOSE,
// D-CBG-34; backend `agent_docs/contracts/board-ai-proposals.md` §6, §7): a proposed member on the
// roster page, a batch of past records on its own page, where they wait, the lineage a record and a
// seat keep, and the by-hand form's file.
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the screens with every word a draft (2026-10-18 17:30 PDT, "1) ok
// 2) yes 3) yes"), then the words in copy round CBG-AI5-PROPOSE-COPY1, every row as recommended
// (in-chat 2026-10-19 23:33 PDT, "looks good";
// `scratchpad/2026-10-19-proposals-copy-round/copy-review.html`). Each line names its row there and
// the frame that draws it, or "not drawn" for a state no frame shows; an N row is a word no design
// page carried. A line marked APPROVED is an approved line reused word for word, and says whose. A
// line added or changed since is marked DRAFT where it sits, until its own round.
//
// CBG-UX11 (D-CBG-39; design-284 `scratchpad/2026-10-24-import-without-batches-design/` §2.4, §10,
// frames c1–c3): the confirmation page names no batch. Its way back, its second act, a record's read,
// the confirmation's lead and the served refusal lose the word; the dialog loses its two batch fields
// (Board fills the batch it keeps internally, backend B6); a record the minute book already holds
// says so quietly. Those lines were drafts until CBG-UX-COPY3 approved them (D-CBG-45, 2026-10-29
// 08:53 PDT, rows I5 and I11–I18), several reworded; each says which row it is. The two dialogs'
// forms for files from the uploads (cut inbox's) were drafts until the folder upload's copy round,
// CBG-AI5-INBOX-COPY1, approved them (D-CBG-46, 2026-10-29 21:31 PDT, rows C2 and C3).
//
// CBG-FIX160's words (cut aitoolsfit: §10, and the Word clauses of 4.2 and 9.4) were drafts until
// D-CBG-50 approved them as built (Jonathan, in-chat 2026-10-31 08:06 PDT, "I agree with your recs,
// please handle it").
//
// D-CBG-45 as amended (2026-10-29 08:57 PDT, Jonathan: "anywhere the copy named Claude should be
// changed to generic AI assistant"): wherever a line names the AI that acted, it says "AI
// assistant", never the connected tool's name: "An AI assistant …" where a page first names it,
// "the AI assistant" after, and never "your", since the reader is often not the person whose
// connector proposed.
//
// The copy gives each count's plural; its singular ("1 record", "Import this record?", "goes in
// without its file") is the harness's own (`design/board-ai-proposals`), as 8.1's "Discard it" is;
// 2.4's and 6.2's are the copy round's.
//
// ⚠ What binds every line: plan §3.3 (no AI output is the record until a person's act makes it so),
// §3.4's bans (nothing "verified", nothing "certified"), and D-CBG-34's four readings (proposing a
// member is not adding one; a past record naming people gives none of them a seat or access). No
// pronoun for a named person.

/** "{n} record" / "{n} records". */
function records(n: number): string {
  return `${n} ${n === 1 ? 'record' : 'records'}`;
}

/** The AI that acted, where a page first names it (D-CBG-45 as amended): opening a sentence, and
 *  inside one. */
const AN_AI = 'An AI assistant';
const AN_AI_INSIDE = 'an AI assistant';

/** The AI that acted, once its page has named it. */
const THE_AI = 'The AI assistant';
const THE_AI_INSIDE = 'the AI assistant';

/** A person the directory gives no name for (the house's words). */
const UNNAMED_PERSON = 'someone whose name isn’t shown';

/**
 * A person's name before a date: the unnamed form is set off by a comma, so the date never reads as
 * part of it (copy 7.1, 7.2; CBG-YR2-COPY2's rule).
 */
const beforeADate = (name: string | null): string => name ?? `${UNNAMED_PERSON},`;

/**
 * Whose connection a proposal came through (copy 1.2, 2.3, 3.2, 7.1): the reader's own, a person the
 * directory names, or someone it gives no name for (N5).
 */
export type ProposedThrough =
  | { kind: 'yours' }
  | { kind: 'named'; name: string }
  | { kind: 'unnamed' };

/** Copy N5: the unnamed person goes after the noun, never "…isn’t shown’s connection". */
function connection(by: ProposedThrough): string {
  switch (by.kind) {
    case 'yours':
      return 'your connection';
    case 'named':
      return `${by.name}’s connection`;
    case 'unnamed':
      return `the connection of ${UNNAMED_PERSON}`;
  }
}

export const proposalWords = {
  // ── 1. A proposed member, on the roster page (frames r1–r3) ──────────────────────────────────

  /** Copy 1.1 (r1): the card's heading. */
  membersHeading: 'Proposed members',
  /** Copy 1.2 (r1): the card's line. */
  membersLead: (by: ProposedThrough) =>
    `${AN_AI} proposed these through ${connection(by)}. They aren’t on the board until you add them.`,
  /** Copy 1.3 (r1): each row's two buttons. */
  reviewAndAdd: 'Review and add',
  discard: 'Discard',
  /** CBG-UX12 (Jonathan's words, 2026-10-29 11:41 PDT, so not a draft): the AI's own note on a
   *  proposal, folded behind one link until asked for (`ai-rationale.tsx`), which names it, so the
   *  note takes no label of its own; and the link at its foot that folds it away. */
  readRationale: 'Read the AI assistant’s rationale',
  closeRationale: 'Close',
  /** Copy 1.5 (r2): the form's line under "Add a board member". */
  formFromProposal: `${THE_AI} proposed this member. Check each detail, then add them.`,
  /** Copy 1.6 (r2): under the titles, a proposed title the board doesn't have; one line for each
   *  such title, so the copy's sentence stays whole. */
  titleNotOnBoard: (label: string) =>
    `${THE_AI} also suggested ${label}, which isn’t one of this board’s titles.`,
  /** Copy 1.7 (r3): discarding, the dialog. */
  discardMemberTitle: 'Discard this proposal?',
  discardMemberBody: (name: string) =>
    `${name} won’t be added to the board. ${THE_AI} can propose them again.`,
  keepIt: 'Keep it',
  /** Copy 1.8 (not drawn): after a discard. */
  memberDiscarded: 'Proposal discarded.',
  /** Copy 1.9 (not drawn): the add or the discard refused, decided elsewhere meanwhile. */
  memberProposalDecided:
    'This proposal was already added or discarded. Reload the page to see the board as it is.',

  // ── 2. Where proposed records wait (frames i1, m1, and the menu's counts) ────────────────────

  /** Copy 2.1 (i1): the import page's card heading. */
  waitingHeading: 'Waiting for you',
  /** Copy 2.2 (i1): the card's line; `missing` counts the records with no file yet. Approved
   *  (CBG-UX-COPY3's I11, kept). */
  batchWaiting: (n: number, missing: number) =>
    `${AN_AI} proposed ${records(n)} for your minute book.${
      missing === 0
        ? ''
        : missing === 1
          ? ' 1 doesn’t have its file yet.'
          : ` ${missing} don’t have their file yet.`
    }`,
  /** Copy 2.3 (i1): the card's quiet line. */
  proposedOn: (by: ProposedThrough, date: string) => `Through ${connection(by)} · ${date}`,
  /** Copy 2.4 (i1, m1): the button, on both; its one-record form the copy round's. */
  reviewThem: (n: number) => (n === 1 ? 'Review it' : 'Review them'),
  /** Copy 2.5 (m1): the Minute book's line, above the records. */
  bookWaiting: (n: number) =>
    `${records(n)} ${AN_AI_INSIDE} proposed ${n === 1 ? 'is' : 'are'} waiting for you to review.`,

  // ── 3. The batch's page (frames b1, b1b, b2) ─────────────────────────────────────────────────

  /** Copy 3.1 (b1): the page's title. */
  batchTitle: `Records ${AN_AI_INSIDE} proposed`,
  /** Copy 3.2 (b1): the page's line. Approved in CBG-UX-COPY3 (the orchestrator's fourth point):
   *  "from your files", true of a folder upload too, whose files are given to Givmo, not the AI. */
  batchLead: `${THE_AI} proposed these from your files. Nothing here is in your minute book until you confirm it.`,
  /** Approved (CBG-UX-COPY3's I12; review-295's M1, c1): under the facts, how to read what the AI
   *  wrote about a record before confirming it, naming the thing to select (a row's title opens
   *  it). */
  readBeforeConfirming: `Select a record’s title to read what ${THE_AI_INSIDE} wrote about it before you confirm.`,
  /** Copy 3.3 (b1): the facts under it. */
  batchFacts: (n: number, attached: number, date: string) =>
    `${records(n)} · ${attached} ${attached === 1 ? 'file' : 'files'} attached · proposed ${date}`,
  /** Copy 3.4 (b1, b2): after the list, above the two buttons, while any file is missing. Approved
   *  in CBG-UX-COPY3 (I15, I5): what happened, without fault, and what a file can't do later. */
  missingCallout: (missing: number, of: number) =>
    missing === of
      ? `${THE_AI} listed these files but didn’t send them. Add each one before you confirm: a file can’t be added once a record is in your minute book.`
      : missing === 1
        ? `1 record doesn’t have its file yet: ${THE_AI_INSIDE} listed the file but didn’t send it. Add each one before you confirm: a file can’t be added once a record is in your minute book.`
        : `${missing} records don’t have their file yet: ${THE_AI_INSIDE} listed the files but didn’t send them. Add each one before you confirm: a file can’t be added once a record is in your minute book.`,
  /** Copy 3.5 (b1): the two buttons, after the last record. Approved (CBG-UX-COPY3's I17; CBG-UX11,
   *  c1; review-295's M2): the second names its scope as the first does, "Discard all {n}", its
   *  one-record form "Discard it" (8.1's own), so it never reads as the records without a file
   *  named above it. */
  confirmButton: (n: number) => `Confirm and import ${records(n)}`,
  discardButton: (n: number) => (n === 1 ? 'Discard it' : `Discard all ${n}`),
  /** Approved (CBG-UX-COPY3's I13): the page's way back, naming the import page's own title (c1);
   *  "Back to" makes it a way back, not an act. */
  backToImport: 'Back to Import older records',
  /** Approved (CBG-UX-COPY3's I17; CBG-UX11): the page's title while it loads or can't be read, and
   *  the line then. */
  recordsToConfirm: 'Records to confirm',
  couldNotOpenRecords: 'We couldn’t open these records.',
  /** Approved (CBG-UX-COPY3's I14; CBG-UX11, c1, c2; review-295's M3): a record the minute book
   *  already holds, before the book's own title, which links to it, then what confirming does and
   *  the way out. Never a refusal: the person leaves it out or imports it anyway. No-break spaces
   *  either side of "⋯" keep "its ⋯ menu" on one line. */
  alreadyInTheBook: 'Already in your minute book: ',
  alreadyInTheBookAfter: '. Confirming adds it again; leave it out from its\u00a0⋯\u00a0menu.',
  /** Copy 3.7 (b1, b2): a missing file, the page's one coloured word. */
  noFileYet: 'No file yet',
  addTheFile: 'Add the file',
  /** Copy 3.8 (b1, b2): why it's missing, named and not arrived. Approved in CBG-UX-COPY3 (I11):
   *  "listed … as its file" can't be read as a rename, and ends on a word. */
  fileNamed: (file: string) => `${THE_AI} listed “${file}” as its file.`,
  /** Copy 3.9 (b1): why, too large (the copy of the return's approved shape). */
  fileTooLarge: (file: string) => `“${file}” is larger than 50 MB. Add a smaller copy.`,
  /** Copy 3.10 (not drawn): why — none named, the wrong kind, unreadable. */
  fileNotNamed: `${THE_AI} didn’t name a file for this one.`,
  fileWrongType: (kind: string) => `What arrived wasn’t the ${kind} it was named as.`,
  fileUnreadable: 'Board couldn’t read what arrived.',
  /** Copy 3.11 (b8): a row's menu. Its "Read it" left it (CBG-UX13): the file's name and the title
   *  read the record. */
  replaceTheFile: 'Replace the file',
  leaveOut: 'Leave this one out',

  // ── 4. Adding a missing file (frame b3) ──────────────────────────────────────────────────────

  /** Copy 4.1 (b3): the dialog. */
  addFileTitle: 'Add the file',
  addFileFor: (title: string) => `For “${title}”.`,
  addFileNamed: (file: string) => `${THE_AI} listed “${file}” as its file.`,
  /** Copy 4.2 (b3): what Board takes; a Word file since CBG-FIX160 H (D-CBG-47), approved in
   *  D-CBG-50. */
  addFileKinds: 'A PDF, a Word file or a picture (JPEG, PNG, HEIC or TIFF), up to 50 MB.',
  /** Copy 4.3 (b3): the button and its waits. */
  chooseFile: 'Choose a file',
  uploading: 'Uploading…',
  checking: 'Checking the file…',
  /** Copy 4.4 (not drawn): a chosen file over 50 MB, before it uploads. */
  chosenTooLarge: 'That file is larger than 50 MB. Choose a smaller copy.',
  /** Copy 4.5 (not drawn): Board's check turns down what uploaded. */
  chosenWrongType: (kind: string) => `That file isn’t the ${kind} its name says. Choose another.`,
  chosenUnreadable: 'Board couldn’t read that file. Choose another.',

  // ── 5. Reading one proposed record (frame b4) ────────────────────────────────────────────────

  /** Copy 5.1 (b4): under the title. Approved (CBG-UX-COPY3's I17; CBG-UX11): no batch. */
  wordForWord: 'If you confirm, this goes into your minute book word for word.',
  /** Copy 5.2 (b4): the parts. */
  whereFrom: 'Where it came from',
  whatItSays: 'What it says',
  theFile: 'The file',
  /** Copy 5.3 (not drawn): no text; "It’s in the file." only where a file was named. */
  noText: (fileNamed: boolean) =>
    fileNamed
      ? `${THE_AI} didn’t include the text. It’s in the file.`
      : `${THE_AI} didn’t include the text.`,
  /** Copy 5.4 (b4, and a record's page): the file's link, and the button. */
  openTheFile: 'Open the file',
  close: 'Close',

  // ── 6. The confirmation (frame b5) ───────────────────────────────────────────────────────────

  /** Copy 6.1 (b5). */
  confirmTitle: (n: number) => `Import ${n === 1 ? 'this record' : `these ${n} records`}?`,
  /** Copy 6.2 (b5): what it does, before the click; its one-record form the copy round's. Approved
   *  in CBG-UX-COPY3 (I5; CBG-UX11, c3): "as one batch" dropped; (review-295's M1) it says each
   *  record carries what the AI wrote about it, which the person may not have opened. */
  confirmLead: (n: number) =>
    n === 1
      ? `It goes into your minute book with what ${THE_AI_INSIDE} wrote about it, exactly as it is here. After that it can’t be changed and no file can be added to it; a mistake is fixed with a correction.`
      : `They go into your minute book together, each with what ${THE_AI_INSIDE} wrote about it, exactly as it is here. After that a record can’t be changed and no file can be added to it; a mistake is fixed with a correction.`,
  /** Copy 6.3 (b5): what goes in without a file, then their titles. */
  withoutFile: (missing: number) =>
    `${missing} ${missing === 1 ? 'goes in without its' : 'go in without their'} file:`,
  /** Approved (CBG-UX-COPY3's I16; CBG-UX11, c3): what the minute book already holds, then their
   *  titles, the way 6.3 names the records going in without their file. (Copy 6.4 and 6.5, the
   *  batch's name and where the batch came from, are gone: Board fills the batch it keeps, backend
   *  B6.) */
  alreadyInTheBookCount: (n: number) =>
    n === 1
      ? '1 is already in your minute book, and would go in twice:'
      : `${n} are already in your minute book, and would go in twice:`,
  /** Approved in CBG-UX-COPY3 (I16, a new line): after their titles, the way out, where the dialog
   *  names the duplicate; "its ⋯ menu" kept on one line. */
  leaveDuplicatesOut: (n: number) =>
    n === 1
      ? 'To leave it out, choose Cancel, then “Leave this one out” from its\u00a0⋯\u00a0menu.'
      : 'To leave them out, choose Cancel, then “Leave this one out” from each one’s\u00a0⋯\u00a0menu.',
  /** Copy 6.6 (b5): the buttons. */
  cancel: 'Cancel',
  importButton: (n: number) => `Import ${records(n)}`,
  /** Copy 6.7 (not drawn): after it. */
  imported: (n: number) => `${records(n)} imported into your minute book.`,
  /** Copy 6.8 (not drawn): refused, the records changed meanwhile — the backend's sentence, which
   *  the page shows as served on every act it refuses. Approved (CBG-UX-COPY3's I17; CBG-UX11,
   *  backend B7): no batch. */
  batchChanged:
    'Something changed in these records since you opened them. Reload the page and look again.',

  // ── 7. After, and the history (frame b6; not drawn) ──────────────────────────────────────────

  /** Copy 7.1 (b6): the imported batch's card. */
  importedFromLine: (by: ProposedThrough, date: string) =>
    `${AN_AI} proposed these through ${connection(by)}${by.kind === 'unnamed' ? ',' : ''} on ${date}.`,
  /** Copy 7.2 (not drawn): a record's page, its provenance; `null` for a name not shown. Approved
   *  in CBG-UX-COPY3 (I18): who proposed it, through whose connector, and the person's act that
   *  put it in the book, in the import page sentence's own words; "an AI tool" goes with the tool's
   *  name, since "an AI assistant" says it. The unnamed proposer goes after the noun (copy N5). */
  recordLineage: (
    proposer: string | null,
    proposedOn: string,
    importer: string | null,
    importedOn: string,
  ) =>
    `Proposed by ${AN_AI_INSIDE} through ${
      proposer !== null
        ? `${proposer}’s Givmo connector`
        : `the Givmo connector of ${UNNAMED_PERSON},`
    } on ${proposedOn} · confirmed by ${beforeADate(importer)} on ${importedOn}`,
  /** Copy 7.3 (not drawn): a seat's history, after its "Added …" line. Approved in CBG-UX-COPY3
   *  (I18): the same phrase as a record's. */
  seatLineage: (proposer: string, date: string) =>
    ` · proposed by ${AN_AI_INSIDE} through ${proposer}’s Givmo connector on ${date}`,
  /** Approved in CBG-UX-COPY3 (I19): an imported record's history, when an AI proposed it, names
   *  the same two parties as the card above it. A record entered by hand keeps "By {name}". */
  confirmedFromProposal: (name: string) => `Confirmed by ${name}, from ${THE_AI_INSIDE}’s proposal`,

  // ── 8. Discarding the batch, leaving one out (frames b7, b8) ─────────────────────────────────

  /** Copy 8.1 (b7): the batch. */
  discardBatchTitle: (n: number) => `Discard ${n === 1 ? 'this record' : `these ${n} records`}?`,
  discardBatchBody: `Nothing goes into your minute book, and every file attached to them is deleted. ${THE_AI} can propose them again.`,
  /** Approved in CBG-AI5-INBOX-COPY1 (C3; cut inbox's review fold, review-309 F1): the same dialog
   *  when files came from the uploads, which Board keeps and sends back there: every attached file
   *  (`every`), or some. "An AI assistant": the reader may not be the person whose AI proposed. */
  discardBatchBodyUploads: (every: boolean) =>
    every
      ? `Nothing goes into your minute book, and their files go back to your uploads. ${AN_AI} can propose them again.`
      : `Nothing goes into your minute book. Files from your uploads go back there, and every other file attached to them is deleted. ${AN_AI} can propose them again.`,
  keepThem: 'Keep them',
  discardAll: (n: number) => (n === 1 ? 'Discard it' : `Discard all ${n}`),
  /** Copy 8.2 (b8): one record. */
  leaveOutTitle: 'Leave this one out?',
  leaveOutBody: (title: string, withFile: boolean) =>
    `“${title}” won’t be imported${withFile ? ', and its file is deleted' : ''}. ${THE_AI} can propose it again.`,
  /** Approved in CBG-AI5-INBOX-COPY1 (C2; review-309 F1): one record whose file came from the
   *  uploads, which Board keeps. "An AI assistant", as C3 says it. */
  leaveOutBodyUpload: (title: string) =>
    `“${title}” won’t be imported, and its file goes back to your uploads. ${AN_AI} can propose it again.`,
  leaveItOut: 'Leave it out',

  // ── 9. The by-hand form's file (frame h1) ────────────────────────────────────────────────────

  /** Copy 9.1 (h1): the field's label, in the reference field's place. */
  byHandFileLabel: 'The file (optional)',
  /** Copy 9.3 (h1): a chosen file's act. */
  remove: 'Remove',
  /** Copy 9.4 (h1): the note under it. Approved in CBG-UX-COPY3 (I5): the file optional, its limit
   *  said plainly, and whose copy it is. A Word file since CBG-FIX160 H (D-CBG-47), approved in
   *  D-CBG-50. */
  byHandFileNote:
    'A PDF, a Word file or a picture (JPEG, PNG, HEIC or TIFF), up to 50 MB. Board keeps a copy with the record. A file can’t be added later, so add it now if you have one.',
  /** APPROVED (copy 9.5; not drawn): the copy of the return's words while its file goes up. */
  addingTheFile: 'Adding the file…',
  /** APPROVED (copy 9.6; not drawn): the copy of the return's words when a file didn't arrive. */
  fileDidNotArrive: 'We couldn’t add that file. Please try again.',

  // ── 10. CBG-FIX160 (D-CBG-47; design-320 §4, §6, §8, frames l1–l4, g1, i1, i2) ────────────────
  //
  // Every line here is approved as built (D-CBG-50, Jonathan, in-chat 2026-10-31 08:06 PDT).

  /** L (l1): a record's links, each its label, then the linked record's title and where it is. */
  adoptedBy: 'Adopted by:',
  aboutTheMeeting: 'About the meeting:',
  replaces: 'Replaces:',
  proposedAbove: ', proposed above',
  inYourMinuteBook: ', in your minute book',
  /** L: a minute-book record whose title the reader isn't served (one they may not open). */
  aRecordInTheBook: 'a record in your minute book',
  /** L (§8.3): a link to a record the person left out, in its row's place. */
  adoptedByLeftOut: 'a record you left out, so it goes in without saying what adopted it.',
  meetingLeftOut: 'a record you left out, so it goes in without naming its meeting.',
  /** L (l2): the leave-out dialog, for each waiting record that names this one. */
  namesThisAsAdopting: (title: string) =>
    `“${title}” names this record as the act that adopted it. If you leave this one out, that record still goes in, without saying what adopted it.`,
  namesThisAsMeeting: (title: string) =>
    `“${title}” names this record as its meeting. If you leave this one out, that record still goes in, without naming its meeting.`,
  /** L (l3): leaving out a version others replace: with every version that replaces it. */
  leaveOutVersionsTitle: (n: number) =>
    n === 2 ? 'Leave out both versions?' : `Leave out all ${n} versions?`,
  replacesThisVersion: (newer: string) =>
    `“${newer}” replaces this version, so this one can be left out only with it.`,
  versionsLeftOut: (titles: readonly string[], files: 'none' | 'deleted' | 'uploads' | 'mixed') =>
    `${listOf(titles.map((title) => `“${title}”`))} won’t be imported${
      files === 'deleted'
        ? ', and their files are deleted'
        : files === 'uploads'
          ? ', and their files go back to your uploads'
          : ''
    }.${
      files === 'mixed'
        ? ' Files from your uploads go back there, and every other file attached to them is deleted.'
        : ''
    } ${THE_AI} can propose them again.`,
  leaveOutVersions: (n: number) => (n === 2 ? 'Leave out both' : `Leave out all ${n}`),
  /** L (l4): the confirmation, the records going in without a link, then their titles. */
  withoutLink: (n: number) =>
    n === 1
      ? `1 goes in without a link ${THE_AI_INSIDE} proposed, because you left out the record it names:`
      : `${n} go in without a link ${THE_AI_INSIDE} proposed, because you left out the records they name:`,

  /** G (g1): which pages of a signed file holding several records are this record's, and of how
   *  many when Board counted them. */
  filePages: (first: number, last: number, of: number | null) =>
    first === last
      ? `Page ${first}${of !== null ? ` of ${of}` : ''} is this record’s.`
      : `Pages ${first}–${last}${of !== null ? ` of ${of}` : ''} are this record’s.`,
  sameFileWith: (others: number) =>
    `The same file goes with ${others} other ${others === 1 ? 'record' : 'records'} here.`,
  /** G: a record's page, its file a signed set kept whole. */
  recordFilePages: (first: number, last: number) =>
    first === last
      ? `Page ${first} of the attached file is this record’s.`
      : `Pages ${first}–${last} of the attached file are this record’s.`,

  /** I (i1): a proposed member the AI gave no email of record for. */
  noEmailYet: 'No email address yet',
  /** I (i2): the add form's line for one. */
  formNoEmail: `${THE_AI} proposed no email address. Add the one they want board notices sent to.`,

  /** H: a Word file, which a browser can't open, downloads (D-CBG-47: never shown inline). */
  downloadTheFile: 'Download the file',
} as const;

/** "“A”", "“A” and “B”", "“A”, “B” and “C”". */
function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** CBG-FIX160 H: the Word types, a `.docm` among them until its check keeps it as a `.docx`. */
const WORD_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-word.document.macroenabled.12',
]);

/** Whether a file is a Word file (CBG-FIX160 H): it downloads, and is never shown inline. */
export function isWordFile(contentType: string | null | undefined): boolean {
  return typeof contentType === 'string' && WORD_TYPES.has(contentType);
}

/** The kind a file was named as, in the word a person knows it by (copy's `{kind}`). */
export function fileKindWord(contentType: string | null | undefined): string {
  if (isWordFile(contentType)) return 'Word document';
  switch (contentType) {
    case 'image/jpeg':
      return 'JPEG';
    case 'image/png':
      return 'PNG';
    case 'image/heic':
      return 'HEIC';
    case 'image/tiff':
      return 'TIFF';
    default:
      return 'PDF';
  }
}
